import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useVoiceCapture } from "./useVoiceCapture";
import { useWakeWord, wakeSupported, fold } from "./useWakeWord";
import { DraftDeleteDialog } from "../AdmissionDrafts";

const Ctx = createContext(null);
export const useAssistant = () => useContext(Ctx);
const MUTE_KEY = "dp_voice_muted";
const YES = /^(sim|pode|claro|quero|continuar|continua|isso|ok|descartar|descarta)\b/;
const MIC_ERR = (e) => (e?.name === "NotAllowedError" ? "Permissão do microfone negada. Libere o microfone no navegador ou use o teclado da barra." : "Microfone indisponível neste navegador. Use o teclado da barra.");
const STOP = /^(pode )?(parar|pare|para de falar|pare de falar|chega|silencio|fica quieta|fique quieta|ficar quieta|quieta|cala a boca|para)$/;
const PAUSE = /\b(terminar por enquanto|termina por enquanto|pausar|pausa|depois eu continuo|depois continuo|vou parar por aqui|parar por enquanto)\b/;
const CONT = /^(vamos )?(continuar|continua|continue|pode continuar)$/;
// Removes the assistant name from a sentence ("Não, Joaninha, eu falei...").
const stripName = (t, name) => {
  const k = fold(name);
  if (!k) return t;
  const n = k.split(" ").length;
  const w = t.split(/\s+/);
  for (let i = 0; i + n <= w.length; i++) if (w.slice(i, i + n).map(fold).join(" ") === k) return [...w.slice(0, i), ...w.slice(i + n)].join(" ").replace(/^[\s,.!?;:]+/, "").replace(/\s+([,.!?])/g, "$1").trim();
  return t;
};
const ORD = [["1", "um", "uma", "primeiro", "primeira"], ["2", "dois", "duas", "segundo", "segunda"], ["3", "tres", "terceiro", "terceira"], ["4", "quatro", "quarto", "quarta"], ["5", "cinco", "quinto", "quinta"]];
// Picks a draft only when the answer is unambiguous (number or name); never guesses.
const pickDraft = (list, f) => {
  const words = f.split(" ");
  const byNum = ORD.findIndex((o) => o.some((w) => words.includes(w)));
  if (byNum >= 0 && list[byNum]) return list[byNum];
  const full = list.filter((d) => d.name && f.includes(fold(d.name)));
  if (full.length === 1) return full[0];
  const part = list.filter((d) => fold(d.name).split(" ").some((w) => w.length > 2 && words.includes(w)));
  return part.length === 1 ? part[0] : null;
};

// Global assistant engine: name activation, conversation, controlled actions on the registered form.
export function AssistantProvider({ enabled, children }) {
  const { user, reload } = useAuth();
  const nav = useNavigate();
  const name = user?.assistant?.name || "Assistente";
  const prof = user?.assistant || {};
  const [override, setOverride] = useState(null);
  const aiOn = !!enabled && (override ?? (prof.enabled === true || (prof.enabled == null && !!prof.onboarded)));
  const aiRef = useRef(aiOn);
  aiRef.current = aiOn;
  const [mic, setMic] = useState("unknown");
  const [log, setLog] = useState([]);
  const [busy, setBusy] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [muted, setMuted] = useState(() => localStorage.getItem(MUTE_KEY) === "1");
  const [micOff, setMicOff] = useState(false);
  const [err, setErr] = useState("");
  const [awaiting, setAwaiting] = useState(null);
  const [formType, setFormType] = useState(null);
  const [formDraft, setFormDraft] = useState(null);
  const [resumeList, setResumeList] = useState([]);
  const [delList, setDelList] = useState([]);
  const [delDraft, setDelDraft] = useState(null);
  const [empList, setEmpList] = useState([]);
  const searchAt = useRef(0);
  const [form, setForm] = useState(null);
  const [panel, setPanel] = useState(false);
  const [card, setCard] = useState(null);
  useEffect(() => { if (!card) return undefined; const t = setTimeout(() => setCard(null), 30000); return () => clearTimeout(t); }, [card]);
  const formRef = useRef(null);
  const pending = useRef(null);
  const audio = useRef(null);
  const queue = useRef(Promise.resolve());
  const gen = useRef(0);
  const last = useRef(Date.now());
  const awaitRef = useRef(null);
  const switchTo = useRef(null);
  const [create, setCreateS] = useState(null);
  const creating = useRef(null);
  const setCreate = (c) => { creating.current = c; setCreateS(c); };
  const lastFields = useRef([]);
  const editRef = useRef(null);
  const resumeAfter = useRef(false);
  const interrupted = useRef(false);
  const speakRef = useRef(false);
  const [editing, setEditing] = useState(false);
  awaitRef.current = awaiting;
  const push = (role, text) => text && setLog((l) => [...l.slice(-40), { role, text }]);

  const speakNow = async (text, g, force) => {
    while (editRef.current) await new Promise((ok) => setTimeout(ok, 200));
    if (g !== gen.current || muted || !text || (!aiRef.current && !force)) return;
    setSpeaking(true); speakRef.current = true;
    try {
      capRef.current.pause();
      const { data } = await api.post("/voice/speak", { text }, { responseType: "blob" });
      while (editRef.current && g === gen.current) await new Promise((ok) => setTimeout(ok, 200));
      if (g !== gen.current) return;
      const a = new Audio(URL.createObjectURL(data)); audio.current = a;
      await new Promise((ok) => { a.onended = ok; a.onerror = ok; a.onpause = ok; a.play().catch(ok); });
    } catch { /* voice output is optional */ } finally {
      audio.current = null; setSpeaking(false); speakRef.current = false; last.current = Date.now();
      if (capRef.current.state === "paused" && !editRef.current) capRef.current.resume();
    }
  };
  const say = (text, force = false) => { const g = gen.current; queue.current = queue.current.then(() => speakNow(text, g, force)); return queue.current; };
  // Silent filling: everything is shown on screen; only "Pode começar." is spoken (via say).
  const reply = (text) => { push("assistant", text); return Promise.resolve(); };
  const stopSpeaking = () => { gen.current += 1; audio.current?.pause(); };
  const openForm = (type, draftId = null) => { setPanel(false); setFormDraft(draftId); setFormType(type); };
  // Voice never deletes: it only opens the visual confirmation.
  const askDelete = (d) => { setAwaiting(null); setDelList([]); setDelDraft(d); push("assistant", "Confirme a exclusão na tela."); };
  const resumeDraft = (d) => { setAwaiting(null); setResumeList([]); openForm(d.form || "admissao", d.id); return reply(`Abrindo o rascunho${d.name ? ` de ${d.name}` : ""}. Vamos continuar de onde parou.`); };

  const confirmSubmit = async () => {
    const cfg = formRef.current;
    if (!cfg) return;
    setAwaiting(null); push("assistant", "Enviando a solicitação…"); setBusy(true);
    const r = await cfg.submit();
    setBusy(false);
    reply(r ? `Pronto! Solicitação enviada para a equipe DPdigi. Protocolo ${r.protocol}.` : "Não consegui enviar. Confira os avisos na tela.").then(() => { if (r) toSilence(); });
  };
  const toSilence = () => { capRef.current.stop(); setAwaiting(null); setResumeList([]); };

  const interpret = async (t) => {
    const sent = formRef.current;
    const { data } = await api.post("/voice/interpret", { form: sent.form, step: null, text: t, values: sent.values, options: sent.options, history: log.slice(-6), last_fields: lastFields.current, manual_fields: sent.manual || [], skipped: sent.skipped || [], active_field: sent.active || "" });
    const cfg = formRef.current;
    if (!aiRef.current || cfg?.form !== sent.form) return;
    cfg.onApply(data);
    const got = Object.keys(data.updates || {}).length || (data.skip || []).length || data.create || (data.action && data.action !== "none");
    const clarify = !got && /\?/.test(data.reply || "") && (data.reply || "").length > 40;
    if (data.errors?.length) { setErr(data.errors[0]); cfg.onMiss?.(); } else if (!got && !clarify) { setErr("Não consegui entender. Tente novamente."); cfg.onMiss?.(); } else setErr("");
    if (Object.keys(data.updates || {}).length) { setAwaiting(null); lastFields.current = Object.keys(data.updates); }
    const a = data.action;
    if (a === "switch") { switchTo.current = data.switch_to; setAwaiting("switch"); return reply(data.reply); }
    if (a === "create" && data.create) { setCreate({ ...data.create, stage: "confirm" }); setAwaiting("create"); return reply(data.reply); }
    if (a === "confirm") {
      if (awaitRef.current !== "submit") { cfg.onAction?.("summary"); return reply("Antes de enviar, confira o resumo. Diga: mostre o resumo."); }
      return confirmSubmit();
    }
    if (a === "pause") capRef.current.pause();
    if (["next", "back", "summary"].includes(a)) cfg.onAction?.(a);
    if (a === "summary" && data.ready) setAwaiting("submit");
    if (a === "cancel") setAwaiting("cancel");
    return reply(data.reply);
  };

  const command = async (t) => {
    const ctx = Date.now() - searchAt.current < 120000 ? "search" : "";
    setCard(null);
    const { data } = await api.post("/voice/command", { text: t, context: ctx });
    if (!aiRef.current) return;
    if (data.intent === "open_form") { pending.current = data.carry ? t : null; openForm(data.form); push("assistant", "Pode começar."); return say("Pode começar."); }
    if (data.intent === "open_requests") nav("/empresa/solicitacoes");
    if (data.intent === "resume_draft") { setResumeList(data.drafts || []); setAwaiting("resume"); }
    if (data.intent === "delete_draft") {
      const l = data.drafts || [];
      if (l.length === 1) { askDelete(l[0]); return undefined; }
      setDelList(l); setAwaiting("delete");
    }
    // Management queries are visual only: never spoken (no TTS call), even with spoken replies on.
    if (data.intent === "query") { push("assistant", data.reply); setCard(data.card ? { ...data.card, at: new Date() } : null); toSilence(); return undefined; }
    if (data.intent === "employee") {
      searchAt.current = Date.now();
      const list = data.employees || [];
      if (list.length === 1) { nav(`/empresa/funcionarios/${list[0].id}`); return reply(data.reply).then(toSilence); }
      if (list.length > 1) { setEmpList(list); setAwaiting("pick_emp"); }
    }
    return reply(data.reply);
  };
  const openEmp = (e) => { setAwaiting(null); setEmpList([]); searchAt.current = Date.now(); nav(`/empresa/funcionarios/${e.id}`); return reply(`Abrindo o cadastro de ${e.name}.`).then(toSilence); };

  // Auxiliary registration of a missing structure option (AGUARDANDO_CADASTRO): the form stays open and frozen.
  const endCreate = () => { setCreate(null); setAwaiting(null); };
  const nextQ = async (field, value) => {
    const c = formRef.current;
    if (!c) return "";
    try {
      const { data } = await api.post("/voice/next", { form: c.form, values: { ...c.values, [field]: value }, skipped: c.skipped || [] });
      if (data.summary) { c.onAction?.("summary"); setAwaiting("submit"); }
      return data.reply;
    } catch { return ""; }
  };
  const doCreate = async (c) => {
    const cfg = formRef.current;
    const done = c.fem ? "cadastrada" : "cadastrado";
    try {
      const { data: it } = await api.post("/structure", { kind: c.kind, name: c.name, cbo: c.cbo || null, description: c.description || "" });
      cfg?.onCreated?.(c.field, it); endCreate();
      reply(`${c.art.split(" ").slice(1).join(" ").replace(/^./, (x) => x.toUpperCase())} ${it.name} ${done}. ${await nextQ(c.field, it.name)}`);
    } catch (e) {
      const code = e?.response?.status;
      const ex = e?.response?.data?.detail?.existing;
      if (code === 409 && ex) { cfg?.onCreated?.(c.field, ex); endCreate(); reply(`Já existia ${ex.name} no cadastro, então selecionei essa opção. ${await nextQ(c.field, ex.name)}`); return; }
      if (code === 403) { endCreate(); reply(`Esse cadastro precisa ser realizado por um usuário autorizado. ${c.ask}`); return; }
      setCreate({ ...c, stage: "retry" }); reply(`Não consegui cadastrar. ${errMsg(e)} Quer tentar de novo?`);
    }
  };
  const createStep = async (f, t) => {
    const c = creating.current;
    if (!c || !formRef.current) { endCreate(); return; }
    const no = /^(nao|cancela|cancelar)\b/.test(f);
    const skip = no || /^(pular|pula|pode pular|nao sei|sem|nenhum|nenhuma|deixa em branco)\b/.test(f);
    if (c.stage === "confirm") {
      if (/^(escolher|escolha|outra opcao)/.test(f)) { endCreate(); setErr("Escolha uma opção da lista ou diga outro nome."); formRef.current.onFocus?.(c.field); return; }
      if (no) {
        if (c.similar) { setCreate({ ...c, similar: null }); reply(c.offer_new); return; }
        endCreate(); reply(`Certo, não cadastrei. ${c.ask}`); return;
      }
      if (!YES.test(f)) { reply(c.similar ? `Diga sim para usar ${c.similar} ou não.` : "Diga sim para cadastrar ou não para escolher outra opção."); return; }
      if (c.similar) { formRef.current.onPick?.(c.field, c.similar); endCreate(); reply(`${c.similar} selecionado. ${await nextQ(c.field, c.similar)}`); return; }
      if (c.kind === "cargo") { setCreate({ ...c, stage: "cbo" }); reply("Qual é o CBO do cargo? Se não souber, diga pular."); return; }
      if (c.kind === "escala") { setCreate({ ...c, stage: "desc" }); reply("Quais serão os dias de trabalho e os horários de entrada, saída e intervalo? Se preferir, diga pular."); return; }
      if (c.kind === "jornada") { setCreate({ ...c, stage: "desc" }); reply("Quais serão os horários de entrada, saída e intervalo dessa jornada? Se preferir, diga pular."); return; }
      await doCreate(c); return;
    }
    if (c.stage === "cbo") {
      if (skip) { await doCreate(c); return; }
      const d = t.replace(/\D/g, "");
      if (d.length !== 6) { reply("O CBO tem seis dígitos, por exemplo 2522-10. Pode repetir ou diga pular."); return; }
      await doCreate({ ...c, cbo: `${d.slice(0, 4)}-${d.slice(4)}` }); return;
    }
    if (c.stage === "desc") { await doCreate(skip ? c : { ...c, description: t.slice(0, 300) }); return; }
    if (YES.test(f)) { await doCreate(c); return; }
    endCreate(); reply(`Certo. ${c.ask}`);
  };

  const run = async (fn) => { setBusy(true); setErr(""); try { await fn(); } catch (e) { setErr(errMsg(e)); } finally { setBusy(false); } };

  const handleText = async (raw) => {
    const said = String(raw || "").trim();
    if (!said || !aiRef.current) return;
    last.current = Date.now(); push("user", said);
    const t = stripName(said, name);
    const f = fold(t);
    const cfg = formRef.current;
    if (!f) { reply(cfg ? "Pode começar." : "Olá! Estou aqui."); return; }
    if (f.split(" ").length <= 4 && STOP.test(f)) { stopSpeaking(); return; }
    if (/\b(repete|repita|repetir)\b/.test(f)) { cfg?.onFocus?.(cfg.active); return; }
    if (PAUSE.test(f)) { reply("Pausado. O rascunho fica salvo.").then(toSilence); return; }
    if (cfg && CONT.test(f)) {
      const left = (cfg.fields || []).filter((x) => x.required && !cfg.values?.[x.k]);
      reply(left.length ? `Vamos continuar. Falta: ${left[0].label.toLowerCase()}.` : "Está tudo preenchido. Diga: mostre o resumo.");
      return;
    }
    const aw = awaitRef.current;
    if (aw === "switch") {
      const to = switchTo.current;
      if (/\b(continuar|continua|nao|ficar)\b/.test(f)) { setAwaiting(null); reply("Certo, vamos continuar."); return; }
      const save = /\b(salvar|salva|salve|sim)\b/.test(f);
      const drop = !save && /\b(cancelar|cancela|descartar|descarta)\b/.test(f);
      if (!save && !drop) { reply("Diga salvar, descartar ou continuar."); return; }
      setAwaiting(null);
      if (drop) cfg?.discard(); else cfg?.close();
      openForm(to);
      reply(`${save ? "Rascunho salvo." : "Descartado."} Vou abrir o novo formulário.`);
      return;
    }
    if (aw === "create") { await createStep(f, t); return; }
    if (aw === "cancel") {
      setAwaiting(null);
      const yes = YES.test(f) && !/\bnao\b/.test(f);
      if (yes) Promise.resolve(formRef.current?.discard()).then((ok) => (ok === false ? reply("Não consegui descartar. Tente novamente.") : reply("Pronto, descartei esta solicitação.").then(toSilence)));
      else reply("Certo, vamos continuar.");
      return;
    }
    if (aw === "pick_emp") {
      const pick = /^(nao|na verdade)\b/.test(f) ? null : pickDraft(empList, f);
      if (pick) { openEmp(pick); return; }
      setAwaiting(null); setEmpList([]);
      if (/^(nao|nenhum|nenhuma)$/.test(f)) { reply("Tudo bem."); return; }
    }
    if (aw === "delete") {
      if (/\b(nao|nenhum|nenhuma|cancelar|cancela)\b/.test(f)) { setAwaiting(null); setDelList([]); reply("Tudo bem."); return; }
      const pick = pickDraft(delList, f);
      if (pick) { askDelete(pick); return; }
      reply("Não entendi qual rascunho. Diga o nome, o número, ou toque na opção.");
      return;
    }
    if (aw === "resume") {
      if (/\b(nao|nenhum|nenhuma)\b/.test(f)) { setAwaiting(null); setResumeList([]); reply("Tudo bem."); return; }
      const pick = resumeList.length === 1 && YES.test(f) ? resumeList[0] : pickDraft(resumeList, f);
      if (pick) { resumeDraft(pick); return; }
      reply(resumeList.length === 1 ? "Diga sim para continuar ou não para deixar para depois." : "Não entendi qual rascunho. Diga o nome do funcionário, o número, ou toque na opção.");
      return;
    }
    if (cfg && (!aw || aw === "submit") && controlCommand(cfg, f, aw)) return;
    await run(() => (formRef.current ? interpret(t) : command(t)));
  };

  // Field-navigation commands handled locally; they never become field values.
  const controlCommand = (cfg, f, aw) => {
    const fl = cfg.fields || [];
    if (aw === "submit" && /^(confirmar envio|confirmar o envio|confirmo o envio|confirmo|pode enviar|enviar)$/.test(f)) { confirmSubmit(); return true; }
    if (/^(proximo|proxima|pular|pula|pode pular|pular campo|proximo campo)$/.test(f)) { setErr(cfg.onSkip?.() || ""); return true; }
    if (cfg.active === "benefits" && /^(sim|nao|não|nao quero|sem esse|nao tera|tera sim)$/.test(f)) { setErr(cfg.onSkip?.(!/^nao/.test(f)) || ""); return true; }
    if (/^(voltar|volta|campo anterior|anterior)$/.test(f)) { if (aw === "submit") setAwaiting(null); cfg.onFocus?.(cfg.prev?.()); setErr(""); return true; }
    if (/^(revisar|revisao|finalizar|finaliza|mostrar o resumo|mostre o resumo|terminar)$/.test(f)) {
      const left = fl.filter((x) => x.required && (!cfg.values?.[x.k] || cfg.values[x.k]?.done === false));
      if (left.length) { setErr(`Ainda falta: ${left.map((x) => x.label).join(", ")}.`); return true; }
      cfg.onAction?.("summary"); setAwaiting("submit"); setErr(""); return true;
    }
    const m = f.match(/^(corrigir|corrige|corrija|alterar|altera|altere|mudar|muda|editar|voltar para|volta para|voltar ao|volta ao|voltar a)\s*(?:o |a |os |as |no |na )?(.*)$/);
    if (!m || /\bpara\b/.test(m[2]) || m[2].split(" ").length > 4) return false;
    const rest = m[2].trim();
    const k = !rest || /ultimo campo|ultima/.test(rest) ? lastFields.current[0]
      : fl.find((x) => { const L = fold(x.label).replace(/\s*\(.*\)/, ""); return L === rest || L.startsWith(rest) || rest.startsWith(L) || L.split(" ")[0] === rest.split(" ")[0]; })?.k;
    if (!k) return false;
    if (aw === "submit") setAwaiting(null);
    cfg.onFocus?.(k); setErr(""); return true;
  };

  const segment = async (blob) => {
    if (blob.size < 2500) return;
    last.current = Date.now(); setBusy(true);
    try {
      const fd = new FormData(); fd.append("audio", blob, blob.type.includes("mp4") ? "fala.m4a" : "fala.webm");
      const { data } = await api.post("/voice/transcribe", fd);
      capRef.current.setInterim(""); setBusy(false);
      if (data.text && aiRef.current) await handleText(data.text);
    } catch (e) { setErr(`${errMsg(e)} Você pode usar o teclado da barra.`); setBusy(false); }
  };
  const cap = useVoiceCapture(segment);
  const capRef = useRef(cap);
  capRef.current = cap;

  const startTalk = async (first) => {
    if (micOff || !aiRef.current) return;
    setErr("");
    if (!formRef.current) setPanel(true);
    try { if (capRef.current.state === "off") await capRef.current.start(); setMic("granted"); } catch (e) { if (e?.name === "NotAllowedError") setMic("denied"); setErr(MIC_ERR(e)); setPanel(true); return; }
    last.current = Date.now();
    if (first) handleText(first);
    else reply(formRef.current ? "Estou aqui." : "Olá! Estou aqui.");
  };
  const stopTalk = () => { cap.stop(); stopSpeaking(); };
  useEffect(() => {
    if (aiOn) return;
    capRef.current.stop(); gen.current += 1; audio.current?.pause();
    pending.current = null; setAwaiting(null); setResumeList([]); setPanel(false); setBusy(false); setCard(null);
  }, [aiOn]);
  useEffect(() => {
    let st = null;
    navigator.permissions?.query({ name: "microphone" }).then((s) => { st = s; setMic(s.state); s.onchange = () => setMic(s.state); }).catch(() => {});
    return () => { if (st) st.onchange = null; };
  }, []);
  const setAiEnabled = async (v) => {
    setOverride(v);
    try {
      await api.put("/voice/profile", { enabled: v, ...(v ? { onboarded: true } : {}) });
      await reload();
      if (v) { const msg = `${name} está pronta para ajudar!`; push("assistant", msg); toast.success(msg); say(msg, true); } else toast.info("Assistente de IA desativada. Os formulários continuam funcionando normalmente.");
    } catch (e) { setOverride(null); toast.error(errMsg(e)); }
  };

  const wakeOn = aiOn && !!user?.assistant?.wake && wakeSupported && !micOff;
  const wake = useWakeWord(wakeOn && cap.state === "off" && !speaking, name, (rest) => startTalk(rest));

  useEffect(() => {
    const id = setInterval(() => {
      const idle = formRef.current ? 90000 : 30000;
      if (capRef.current.state === "listening" && !busy && !speaking && Date.now() - last.current > idle) capRef.current.stop();
    }, 5000);
    return () => clearInterval(id);
  }, [busy, speaking]);

  const flushRef = useRef(null);
  flushRef.current = () => { const t = pending.current; if (t && formRef.current) { pending.current = null; run(() => interpret(t)); } };
  const attach = useCallback((k) => setForm(k), []);
  const detach = useCallback((k) => { if (formRef.current?.form === k) formRef.current = null; setForm((f) => (f === k ? null : f)); setAwaiting((a) => (a === "resume" ? a : null)); setLog([]); }, []);
  const flush = useCallback(() => flushRef.current(), []);

  const toggleMute = () => { const m = !muted; setMuted(m); localStorage.setItem(MUTE_KEY, m ? "1" : "0"); if (m) stopSpeaking(); };
  const toggleMicOff = () => { const v = !micOff; setMicOff(v); if (v) stopTalk(); };
  const saveProfile = async (patch) => { await api.put("/voice/profile", patch); await reload(); };
  const editInfo = () => { setAwaiting(null); formRef.current?.onAction?.("edit"); };
  // Manual typing in a field: the assistant stays quiet and stops listening until the edit is finished.
  const beginEdit = (k) => {
    if (editRef.current) return;
    editRef.current = k; setEditing(true);
    if (audio.current || speakRef.current) { interrupted.current = true; stopSpeaking(); }
    if (capRef.current.state === "listening") { capRef.current.pause(); resumeAfter.current = true; }
  };
  const endEdit = () => {
    if (!editRef.current) return;
    editRef.current = null; setEditing(false);
    if (resumeAfter.current) { resumeAfter.current = false; if (capRef.current.state === "paused") capRef.current.resume(); }
  };
  const editIdle = () => { if (!interrupted.current) return; interrupted.current = false; const m = [...log].reverse().find((x) => x.role === "assistant"); if (m) say(m.text); };
  const manualEdit = ({ k, label, text, error, active }) => {
    interrupted.current = false;
    if (!aiRef.current || !formRef.current) return;
    lastFields.current = [k];
    if (!error) setLog((l) => [...l.slice(-40), { role: "user", text: `(corrigi manualmente no formulário: ${label}; esse valor é definitivo)` }]);
    if (active) reply(error || text);
  };
  const announce = (text) => { push("assistant", text); toast.success(text); say(text, true); };
  const instruction = [...log].reverse().find((m) => m.role === "assistant")?.text || "";
  const status = !aiOn ? "disabled" : micOff ? "off" : editing ? "editing" : speaking ? "speaking" : awaiting === "create" ? "creating" : awaiting === "submit" ? "confirm" : busy ? "processing" : cap.state === "listening" ? "listening" : cap.state === "paused" ? "paused" : wake.active ? "wake" : "idle";

  const value = {
    name, aiOn, mic, setAiEnabled, announce, log, busy,
    task: !aiOn ? "inativa" : ["submit", "cancel", "switch", "resume", "pick_emp", "delete"].includes(awaiting) ? "aguardando_confirmacao" : form ? "tarefa_em_andamento" : cap.state !== "off" ? "aguardando_comando" : "inativa", speaking, muted, micOff, err, awaiting, status, form, formRef, formType, setFormType, formDraft, resumeList, resumeDraft, delList, askDelete, empList, openEmp, panel, setPanel, card, setCard, instruction, create,
    interim: cap.interim, level: cap.level, capState: cap.state, profile: user?.assistant || {},
    wake: { supported: wakeSupported, enabled: !!user?.assistant?.wake, active: wake.active, error: wake.error, retry: wake.retry },
    startTalk, stopTalk, stopSpeaking, sendText: handleText, toggleMute, toggleMicOff, confirmSubmit, editInfo, saveProfile, say, attach, detach, flush,
    editing, beginEdit, endEdit, editIdle, manualEdit, editKey: () => editRef.current,
  };
  return <Ctx.Provider value={enabled ? value : null}>{children}{enabled && <DraftDeleteDialog draft={delDraft} onClose={() => setDelDraft(null)} />}</Ctx.Provider>;
}

// Lets an existing form expose its fields/values/actions to the assistant (no parallel form).
export function useAssistantForm(cfg) {
  const a = useContext(Ctx);
  if (a && cfg) a.formRef.current = cfg;
  const key = a && cfg ? cfg.form : null;
  const ready = !!cfg?.ready;
  const attach = a?.attach;
  const detach = a?.detach;
  const flush = a?.flush;
  useEffect(() => { if (!key) return undefined; attach(key); return () => detach(key); }, [key, attach, detach]);
  useEffect(() => { if (key && ready) flush(); }, [key, ready, flush]);
}
